import { forwardRef, useContext, type ReactNode } from 'react';

import { InlineButton } from '../../buttons/inline-button/InlineButton.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { COMPONENT_NAME } from './terminology-overlay-constants.js';
import { TerminologyOverlayContext } from './TerminologyOverlayContext.js';

//#region Typings
/**
 * @public
 */
export interface TerminologyOverlayTriggerProps
  extends
    DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps,
    AriaLabelingProps {
  /**
   * Trigger Overlay button must contain children elements.
   */
  children: ReactNode;
}
//#endregion Typings

/**
 * TerminologyOverlay Trigger component, used for rendering trigger content.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  TerminologyOverlayTriggerProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const {
    'data-dt-name': dataDtName = 'Trigger',
    ...remainingBehavioralTrackingProps
  } = behavioralTrackingProps;
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const overlayTriggerProps = useContext(TerminologyOverlayContext);

  return (
    <InlineButton
      as="button"
      id={propId}
      className={consumerClassName}
      style={consumerStyle}
      ref={forwardedRef}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {..._mergeProps(
        overlayTriggerProps,
        ariaLabelingProps,
        { 'data-dt-children-name': dataDtName },
        remainingBehavioralTrackingProps,
        { 'data-dt-component': COMPONENT_NAME },
      )}
      aria-haspopup="dialog"
    >
      {children}
    </InlineButton>
  );
});

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'TerminologyOverlay.Trigger';
